import { Component } from '@angular/core';
import { ConfirmationService, MessageService } from "primeng/api";
import { 
  QUOTE_COLUMNS, 
  QUOTE_PARAMETERS_COLUMNS, 
  QUOTE_ACTIONS, 
  QUOTE_PARAMETERS_ACTIONS 
} from "@modules/private/quote/data/constants/quote.constants";
import { QuoteService } from "@modules/private/quote/data/services/view/quote.service";
import { QuoteParametersService } from "@modules/private/quote/data/services/view/quote-parameters.service";
import { ApiQuoteService } from "@modules/private/quote/data/services/api/api-quote.service";
import { ApiQuoteParametersService } from "@modules/private/quote/data/services/api/api-quote-parameters.service";
import { AuthService } from '@data/services/modules/auth/auth.service';
import { PdfQuotationApiService } from '@data/services/utils/pdf-quotation-api.service';
import { IQuoteParameters } from "@modules/private/quote/data/interfaces/iquote-parameters.interface";
import { IQuote } from "@modules/private/quote/data/interfaces/iquote.interface";
import { UserRole } from "@data/enums/user-role.enum";

@Component({
  selector: 'app-quote-list',
  templateUrl: './quote-list.component.html',
  styleUrls: ['./quote-list.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class QuoteListComponent {
  // ===== CONFIGURACIÓN DE TABLAS =====
  quoteParametersColumns = QUOTE_PARAMETERS_COLUMNS
  quoteColumns = QUOTE_COLUMNS
  quoteParametersActions = QUOTE_PARAMETERS_ACTIONS
  quoteActions = QUOTE_ACTIONS

  // ===== DATOS DE LAS TABLAS =====
  quoteParametersList: IQuoteParameters[] = []
  quotesList: IQuote[] = []

  // ===== CONFIGURACIÓN DE DIÁLOGOS =====
  createEntity = false
  showQuoteDetail = false
  action = 'CREATE'
  
  // ===== CONFIGURACIÓN DE USUARIO =====
  user: string = ''
  role: string = ''

  // ===== ACCIONES PRINCIPALES PARA QUOTEPARAMETERS =====
  additionalMainActions = [
    { label: 'Nueva cotización', icon: 'fa-solid fa-square-plus', action: 'CREATE' }
  ]

  // ===== CONFIGURACIÓN DE PAGINACIÓN Y FILTROS =====
  // Para QuoteParameters
  totalRecordsParameters = 0;
  loadingParameters = false;
  pageParameters = 1;
  limitParameters = 10;
  filtersParameters = {};

  // Para Quotes
  totalRecordsQuotes = 0;
  loadingQuotes = false;
  pageQuotes = 1;
  limitQuotes = 10;
  filtersQuotes = {};

  // ===== DATOS ACTUALES =====
  selectedQuoteParameters?: IQuoteParameters = undefined
  selectedQuote?: IQuote = undefined
  loading = false;

  // Expone el enum al template HTML (quote-list.component.html usa UserRole.BROKER)
  public UserRole = UserRole

  // Variable para modal de opciones PDF
  showPdfOptionsDialog = false;
  private pendingPdfContext: { quoteData: any; referenceId?: string; onFinally?: () => void } | null = null;

  constructor(
    private confirmationService: ConfirmationService,
    public apiQuoteService: ApiQuoteService,
    public apiQuoteParametersService: ApiQuoteParametersService,
    private pdfQuotationService: PdfQuotationApiService,
    private messageService: MessageService,
    public quoteParametersService: QuoteParametersService,
    private authService: AuthService,
  ) {
    this.user = this.authService.getUser?.user.id || ''
    this.role = this.authService.getUser?.user.permissions.name || ''
  }

  ngOnInit() {
    this.loadQuoteParameters();
    this.loadQuotes();
  }

  // ===== MÉTODOS DE CARGA DE DATOS =====

  loadQuoteParameters() {
    this.loadingParameters = true;
    this.apiQuoteParametersService.getAll(this.pageParameters, this.limitParameters, this.filtersParameters)
      .subscribe({
        next: (response) => {
          this.quoteParametersList = response.data || [];
          this.totalRecordsParameters = response.total || 0;
          this.loadingParameters = false;
        },
        error: (error) => {
          console.error('Error loading quote parameters:', error);
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Error al cargar parámetros de cotización' 
          });
          this.loadingParameters = false;
        }
      });
  }

  loadQuotes() {
    this.loadingQuotes = true;
    this.apiQuoteService.getAll(this.pageQuotes, this.limitQuotes, this.filtersQuotes)
      .subscribe({
        next: (response: any) => {
          this.quotesList = response.data || [];
          this.totalRecordsQuotes = response.totalItems || 0;
          this.loadingQuotes = false;
        },
        error: (error) => {
          console.error('Error loading quotes:', error);
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Error al cargar cotizaciones' 
          });
          this.loadingQuotes = false;
        }
      });
  }

  // ===== MÉTODOS PARA MANEJAR DIÁLOGOS =====

  hideDialog() {
    this.createEntity = false;
    this.quoteParametersService.resetQuoteAmounts();
  }

  hideQuoteDetail() {
    this.showQuoteDetail = false;
    this.selectedQuote = undefined;
  }

  // ===== MÉTODOS PARA ACCIONES PRINCIPALES =====

  mainActions(flow: string) {
    this.quoteParametersService.resetQuoteAmounts();
    this.quoteParametersService.setFormMethod('CREATE');
    this.quoteParametersService.useProvisionalData = false; // Iniciar siempre con datos reales
    this.quoteParametersService.useProvisionalAsset = false; // Activo real por defecto (toggle independiente)
    this.quoteParametersService.initializeForm(); // Usar el nuevo método unificado
    this.createEntity = true;
    this.action = 'CREATE';
  }

  // ===== MÉTODOS PARA ACCIONES DE QUOTEPARAMETERS =====

  quoteParametersActionMethods(data: { event: any, action: string, data: IQuoteParameters }) {
    switch (data.action) {
      case 'VIEW':
        this.viewQuoteParameters(data.data);
        break;
      case 'EDIT':
        this.editQuoteParameters(data.data);
        break;
      case 'CALCULATE':
        this.calculateQuote(data.data._id!);
        break;
      case 'DELETE':
        this.deleteQuoteParameters(data.event, data.data);
        break;
    }
  }

  viewQuoteParameters(quoteParameters: IQuoteParameters) {
    this.selectedQuoteParameters = quoteParameters;
    // Aquí podrías abrir un diálogo de vista detallada si es necesario
    this.messageService.add({ 
      severity: 'info', 
      summary: 'Ver Detalles', 
      detail: `Parámetros ID: ${quoteParameters._id}` 
    });
  }

  editQuoteParameters(quoteParameters: IQuoteParameters) {
    this.quoteParametersService.resetQuoteAmounts();
    this.quoteParametersService.formMethod = 'EDIT';
    this.quoteParametersService.quote = quoteParameters; // Cargar los datos existentes
    this.quoteParametersService.initializeForm(); // Usar el nuevo método unificado
    this.createEntity = true;
    this.action = 'EDIT';
  }

  calculateQuote(parametersId: string) {
    this.loadingQuotes = true;
    this.apiQuoteParametersService.calculateQuote(parametersId)
      .subscribe({
        next: (response) => {
          if (response.error === false) {
            this.messageService.add({ 
              severity: 'success', 
              summary: 'Cotización Calculada', 
              detail: 'La cotización se calculó correctamente' 
            });
            // Recargar las cotizaciones para mostrar la nueva
            this.loadQuotes();
          } else {
            this.messageService.add({ 
              severity: 'error', 
              summary: 'Error', 
              detail: response.message || 'Error al calcular cotización' 
            });
          }
          this.loadingQuotes = false;
        },
        error: (error) => {
          console.error('Error calculating quote:', error);
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Error al calcular cotización' 
          });
          this.loadingQuotes = false;
        }
      });
  }

  deleteQuoteParameters(event: Event, quoteParameters: IQuoteParameters) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: '¿Estás seguro de que deseas eliminar estos parámetros de cotización?',
      header: 'Confirmar Eliminación',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: "p-button-danger p-button-text",
      rejectButtonStyleClass: "p-button-text p-button-text",
      acceptIcon: "none",
      rejectIcon: "none",
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',

      accept: () => {
        this.apiQuoteParametersService.delete(quoteParameters._id!).subscribe({
          next: (response) => {
            if (response.error === false) {
              this.loadQuoteParameters();
              this.messageService.add({ 
                severity: 'success', 
                summary: 'Éxito', 
                detail: 'Los parámetros se eliminaron correctamente' 
              });
            } else {
              this.messageService.add({ 
                severity: 'error', 
                summary: 'Error', 
                detail: response.message || 'No se pudieron eliminar los parámetros' 
              });
            }
          },
          error: (error) => {
            console.error('Error deleting quote parameters:', error);
            this.messageService.add({ 
              severity: 'error', 
              summary: 'Error', 
              detail: 'No se pudieron eliminar los parámetros' 
            });
          }
        });
      },
      reject: () => {
        this.messageService.add({ 
          severity: 'info', 
          summary: 'Cancelado', 
          detail: 'No se eliminaron los parámetros' 
        });
      }
    });
  }

  // ===== MÉTODOS PARA ACCIONES DE QUOTES =====

  quoteActionMethods(data: { event: any, action: string, data: IQuote }) {
    switch (data.action) {
      case 'VIEW':
        this.viewQuote(data.data);
        break;
      case 'DOWNLOAD_PDF':
        this.downloadQuotePDF(data.data);
        break;
    }
  }

  viewQuote(quote: IQuote) {
    this.selectedQuote = quote;
    this.showQuoteDetail = true;
  }

  async downloadQuotePDF(quote: IQuote) {
    this.pendingPdfContext = { quoteData: quote };
    this.showPdfOptionsDialog = true;
  }

  private async executeQuotePdfDownload(quote: IQuote, includeResidual: boolean) {
    this.messageService.add({ 
      severity: 'info', 
      summary: 'Generando PDF', 
      detail: 'Preparando descarga...' 
    });
    
    try {
      await this.pdfQuotationService.generateQuotationPdfById(quote._id ?? '', { includeResidual });
      this.messageService.add({ 
        severity: 'success', 
        summary: 'PDF Generado', 
        detail: 'El PDF se ha descargado correctamente' 
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Error al generar PDF', 
        detail: 'Ocurrió un error al generar el PDF' 
      });
    }
  }

  /**
   * Helper: Muestra diálogo de confirmación de residual y genera PDF
   */
  private confirmResidualAndGeneratePdf(quoteData: any, referenceId?: string, onFinally?: () => void) {
    this.pendingPdfContext = { quoteData, referenceId, onFinally };
    this.showPdfOptionsDialog = true;
  }

  /**
   * Cerrar el diálogo de opciones PDF sin generar nada
   */
  closePdfDialog() {
    this.showPdfOptionsDialog = false;
    const onFinally = this.pendingPdfContext?.onFinally;
    this.pendingPdfContext = null;
    if (onFinally) onFinally();
  }

  /**
   * Generar PDF con la opción de residual seleccionada
   */
  async generatePdf(includeResidual: boolean) {
    this.showPdfOptionsDialog = false;
    const ctx = this.pendingPdfContext;
    this.pendingPdfContext = null;
    if (!ctx) return;

    this.messageService.add({ 
      severity: 'info', 
      summary: 'Generando PDF', 
      detail: 'Preparando descarga...' 
    });

    try {
      // PDF centralizado (ADR-0005): referenceId presente = QuotationParameters; si no, Quotation guardada por id.
      if (ctx.referenceId) {
        await this.pdfQuotationService.generateQuotationPdf(ctx.referenceId, { includeResidual });
      } else {
        await this.pdfQuotationService.generateQuotationPdfById(ctx.quoteData?._id, { includeResidual });
      }
      this.messageService.add({ 
        severity: 'success', 
        summary: 'PDF Generado', 
        detail: 'El PDF se ha descargado correctamente' 
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Error al generar PDF', 
        detail: 'Ocurrió un error al generar el PDF de la cotización' 
      });
    } finally {
      if (ctx.onFinally) ctx.onFinally();
    }
  }

  getAmounts() {
    this.loading = true;
    const data = { ...this.quoteParametersService.entity.value };
    data.user = this.user;
    delete data.id;
    delete data.case
    data.terms = this.quoteParametersService.termsArray.value
    // F-016: solo la vista previa lleva la bandera; saveForm() no la manda (el DTO la rechaza).
    data.advancedRentsIncludeAccessories = this.quoteParametersService.advancedRentsIncludeAccessoriesForPreview();

    // Modo de ACTIVO: provisional → enviar provitionalAsset/Value (sin assets);
    // real → enviar assets (sin provitional*). Habilita cliente real + activo provisional.
    if (this.quoteParametersService.useProvisionalAsset) {
      delete data.assets;
    } else {
      delete data.provitionalValue;
      delete data.provitionalAsset;
    }

    if (data.accessories.length > 0) {
      data.accessories.forEach((accessory: any) => {
        if (accessory.insuranceDetails) {
          if (accessory.insuranceDetails.startPeriod !== null && accessory.insuranceDetails.endPeriod !== null) {
            accessory.insuranceDetails.startPeriod = new Date(accessory.insuranceDetails.startPeriod);
            accessory.insuranceDetails.endPeriod = new Date(accessory.insuranceDetails.endPeriod);
          }
        }
        if (accessory.isInsurance === false) {
          accessory.insuranceDetails = null;
        }
      });
    } else {
      data.accessories = [];
    }

    this.apiQuoteParametersService.getAmounts(data).subscribe(
      (response) => {
        this.loading = false;
        this.quoteParametersService.updateQuoteAmounts(response.data);
        this.messageService.add({ severity: 'info', summary: 'Montos actualizados', detail: 'Los montos se calcularon correctamente' });
      },
      (error) => {
        console.error('Error saving form:', error);
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error al calcular montos', detail: 'Por favor, verifica los datos ingresados' });
      }
    )

    this.loading = false
  }


  saveForm() {
    this.loading = true;
    const data = { ...this.quoteParametersService.entity.value };
    data.user = this.user;
    data.terms = this.quoteParametersService.termsArray.value

    // Modo de ACTIVO: provisional → enviar provitionalAsset/Value (sin assets);
    // real → enviar assets (sin provitional*). Habilita cliente real + activo provisional.
    if (this.quoteParametersService.useProvisionalAsset) {
      delete data.assets;
    } else {
      delete data.provitionalValue;
      delete data.provitionalAsset;
    }

    if (data.accessories.length > 0) {
      data.accessories.forEach((accessory: any) => {
        if (accessory.insuranceDetails) {
          if (accessory.insuranceDetails.startPeriod !== null && accessory.insuranceDetails.endPeriod !== null) {
            accessory.insuranceDetails.startPeriod = new Date(accessory.insuranceDetails.startPeriod);
            accessory.insuranceDetails.endPeriod = new Date(accessory.insuranceDetails.endPeriod);
          }
        }
        if (accessory.isInsurance === false) {
          accessory.insuranceDetails = null;
        }
      });
    } else {
      data.accessories = [];
    }

    if (data.case === 'CREATE' || data.case === 'UNSAVE') {
      data.case = 'CREATE'
      this.apiQuoteParametersService.create(data).subscribe(
        (response: any) => {
          this.hideDialog();
          this.apiQuoteParametersService.getAll()
          this.messageService.add({ severity: 'success', summary: 'Cotización creada', detail: 'La cotización se guardó correctamente' });
          
          // Obtener el ID del quotationParameters creado
          const parametersId = response?.data?._id || response?._id;
          
          if (parametersId) {
            // Calcular la cotización para obtener datos completos para el PDF
            this.apiQuoteParametersService.calculateQuote(parametersId).subscribe({
              next: (calcResponse) => {
                const pdfData = (!calcResponse.error && calcResponse.data) 
                  ? calcResponse.data 
                  : (response?.data || response);
                const cleanup = () => {
                  this.loading = false;
                  this.loadQuoteParameters();
                  this.loadQuotes();
                };
                this.confirmResidualAndGeneratePdf(pdfData, parametersId, cleanup);
              },
              error: (error) => {
                console.error('Error calculating quote for PDF:', error);
                const pdfData = response?.data || response;
                const cleanup = () => {
                  this.loading = false;
                  this.loadQuoteParameters();
                  this.loadQuotes();
                };
                this.confirmResidualAndGeneratePdf(pdfData, parametersId, cleanup);
              }
            });
          } else {
            this.loading = false;
            this.loadQuoteParameters();
            this.loadQuotes();
          }
        },
        (error) => {
          console.error('Error saving form:', error);
        }
      );
    }

    if (data.case === 'EDIT') {
      const id = data.id;
      data.case = 'UPDATE'
      this.apiQuoteParametersService.update(id, data).subscribe(
        (response: any) => {
          this.hideDialog();
          this.apiQuoteParametersService.getAll()
          this.messageService.add({ severity: 'success', summary: 'Cotización actualizada', detail: 'La cotización se actualizó correctamente' });
          
          // Obtener el ID del quotationParameters actualizado
          const parametersId = response?.data?._id || response?._id || id;
          
          // Calcular la cotización para obtener datos completos para el PDF
          this.apiQuoteParametersService.calculateQuote(parametersId).subscribe({
            next: (calcResponse) => {
              const pdfData = (!calcResponse.error && calcResponse.data) 
                ? calcResponse.data 
                : (response?.data || response);
              const cleanup = () => {
                this.loading = false;
                this.loadQuoteParameters();
                this.loadQuotes();
              };
              this.confirmResidualAndGeneratePdf(pdfData, parametersId, cleanup);
            },
            error: (error) => {
              console.error('Error calculating quote for PDF:', error);
              const pdfData = response?.data || response;
              const cleanup = () => {
                this.loading = false;
                this.loadQuoteParameters();
                this.loadQuotes();
              };
              this.confirmResidualAndGeneratePdf(pdfData, parametersId, cleanup);
            }
          });
        },
        (error) => {
          console.error('Error saving form:', error);
          this.messageService.add({ severity: 'error', summary: 'Error al guardar cotización', detail: 'Ocurrió un error al guardar la cotización' });
        }
      )
    }

    this.quoteParametersService.updateQuoteAmounts([{
      term: 0,
      irr: 0,
      advance: 0,
      openingCommission: 0,
      advancedRents: 0,
      advancedRentsNumber: 0,
      advancedRentsAccessories: 0,
      advancedRentsIva: 0,
      advancedRentsTotal: 0,
      ratificationExpenses: 0,
      accessories: [],
      advanceSubtotal: 0,
      advanceIva: 0,
      advanceTotal: 0,
      monthlyCapital: 0,
      monthlyInterest: 0,
      monthlyAccesories: [],
      monthlySubtotal: 0,
      monthlyIva: 0,
      monthlyTotal: 0,
      residuary: 0,
      residuaryIva: 0,
      residuaryTotal: 0,
    }])
  }

}
